import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Http from "../../Http";
import { Insurance_API } from "../../Helper";
import {
  FaPlaneDeparture,
  FaGlobe,
  FaSuitcaseRolling,
  FaRedo,
  FaUsers,
  FaPeopleCarry,
  FaGraduationCap,
  FaUserAlt,
  FaBriefcase,
  FaMapMarkedAlt,
} from "react-icons/fa";
import {
  FaCheckCircle,
  FaSuitcase,
  FaHospital,
  FaPlaneSlash,
  FaPassport,
  FaHeartbeat,
  FaHome,
} from "react-icons/fa";
import TravelCover from "./TravelCover";
import { ArrowRight } from "lucide-react";
import FAQSchema from "../../FAQSchema";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const TravelInsuranceNew = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(
    "Medical & Pre-existing Conditions"
  );
  const faqs = [
    {
      question: "Is a medical check-up necessary to purchase travel insurance?",
      answer:
        "For most insurers, a medical check-up is not required if the applicant is under 70 years of age. However, it is essential to disclose any pre-existing conditions in the proposal form.",
    },
    {
      question: "How is the premium for travel insurance determined?",
      answer:
        "The premium is calculated based on several factors including the insured’s age, the destination, the duration of the trip, and the type of coverage selected.",
    },
    {
      question: "What should I do if I lose my money during the trip?",
      answer:
        "If you lose your travel funds or they are stolen, travel insurance can provide emergency cash assistance.",
    },
    {
      question:
        "Will I receive a refund if I return home earlier than planned?",
      answer:
        "Yes, you may be eligible for a partial refund of your premium if no claims have been made during the policy period.",
    },
    {
      question: "Why is it important to have a travel insurance policy?",
      answer:
        "Travel insurance is crucial for managing unforeseen emergencies abroad. It offers protection against risks such as lost baggage, trip delays, accidents, and lost passports.",
    },
    {
      question: "What is a deductible?",
      answer:
        "A deductible is the amount you are responsible for paying out-of-pocket before the insurance company covers the remaining claim amount.",
    },
    {
      question: "What does travel insurance typically cover?",
      answer:
        "Travel insurance generally covers medical emergencies, trip cancellations or delays, lost baggage, personal liability, and passport loss. Some policies may also include emergency evacuation and accidental death benefits depending on the coverage chosen.",
    },
    {
      question: "Can I buy travel insurance after starting my trip?",
      answer:
        " Most insurers require you to purchase travel insurance before your journey begins. Some international plans may allow mid-trip purchases, but coverage may be limited, and pre-existing incidents might not be covered.",
    },
    {
      question: "Is travel insurance valid for multiple trips?",
      answer:
        " Yes, multi-trip or annual travel insurance plans cover several trips within a specified period, usually a year. These plans are cost-effective for frequent travelers and save the hassle of buying a new policy for each trip.",
    },
    {
      question: "Does travel insurance cover COVID-19 related issues?",
      answer:
        " Many insurers now offer COVID-19 coverage as part of their travel insurance plans, covering hospitalization, trip cancellation due to infection, and quarantine costs. However, coverage varies by policy, so it’s important to check the inclusions.",
    },
    {
      question: "Can I extend my travel insurance policy while abroad?",
      answer:
        "Yes, several insurers allow policy extensions while you are still traveling. You need to request an extension before your current policy expires, and the insurer may require a health declaration or additional premium.",
    },
    {
      question: "Does travel insurance cover adventure sports and activities?",
      answer:
        " Basic travel insurance often excludes risky sports like scuba diving, skiing, or bungee jumping. However, you can opt for special add-ons or adventure sports coverage for protection during such activities, subject to terms and conditions.",
    },
    {
      question: "How do I file a travel insurance claim from another country?",
      answer:
        " To file a claim abroad, contact your insurer’s international helpline immediately. Keep records like medical reports, bills, FIRs (for loss or theft), and boarding passes. You can usually file claims online or via email, depending on the insurer.",
    },
    {
      question: "Is travel insurance mandatory for international trips?",
      answer:
        " While not mandatory for all countries, some destinations—especially in Europe (Schengen visa) and certain Asian nations—require travel insurance with a minimum coverage amount as part of their visa process.",
    },
    {
      question: "Will travel insurance cover pre-existing medical conditions?",
      answer:
        "Most travel insurance plans do not cover pre-existing conditions unless specified. Some policies offer optional coverage for certain conditions if disclosed beforehand, but this often comes with higher premiums or restrictions.",
    },
    {
      question: "What is the difference between single-trip and multi-trip travel insurance?",
      answer:
        " Single-trip insurance provides coverage for one specific journey, ideal for occasional travelers. Multi-trip insurance covers multiple trips in a year, offering convenience and cost savings for business travelers or frequent flyers.",
    },
 
  ];
  const stats = [
    {
      title: "Market Size and Growth",
      description:
        "The India travel insurance market was valued at approximately USD 364.8 million in 2023 and is projected to reach USD 1,005.9 million by 2030, exhibiting a CAGR of 14.0% from 2024 to 2030.",
    },
    {
      title: "Traveler Concerns",
      description:
        "A survey revealed that 35% of insured travelers purchased travel insurance primarily due to concerns about issues like delayed flights or changed reservations, while 29% were worried about potential health emergencies during their trips.",
    },
    {
      title: "Purchasing Behavior",
      description:
        "Approximately 60% of travelers utilized online comparison websites to purchase travel insurance in 2024, indicating a shift towards digital platforms for evaluating and buying insurance policies.",
    },
  ];
  const travelInsuranceData = [
    {
      title: "Domestic Travel Insurance",
      description:
        "Designed for individuals traveling within India, this policy ensures coverage against unexpected incidents such as medical emergencies, personal accidents, trip cancellations, and baggage loss. Whether you’re traveling for leisure, work, or family visits, domestic travel insurance provides financial security against unforeseen disruptions. It also offers compensation for delays in transport, accidental hospitalization, and other travel-related inconveniences.",
      icon: <FaPlaneDeparture className="text-blue-600 text-3xl" />,
    },
    {
      title: "International Travel Insurance",
      description:
        "When traveling abroad, unexpected situations such as medical emergencies, loss of passport, baggage delays, or even trip cancellations can arise. International travel insurance provides financial protection against such mishaps, ensuring that travelers receive timely medical assistance, compensation for flight disruptions, and support for legal or emergency situations overseas. Some policies even cover emergency evacuation and repatriation in case of serious health conditions.",
      icon: <FaGlobe className="text-blue-600 text-3xl" />,
    },
    {
      title: "Single Trip Travel Insurance",
      description:
        "If you’re planning a one-time journey, whether domestic or international, single-trip travel insurance covers you for the duration of that particular trip. The policy starts from the day you depart and lasts until you return. It provides coverage against medical emergencies, trip cancellations, loss of belongings, and travel delays. Ideal for holiday travelers or those traveling for business, this insurance helps mitigate financial risks during a specific trip.",
      icon: <FaSuitcaseRolling className="text-blue-600 text-3xl" />,
    },
    {
      title: "Multi-Trip Travel Insurance",
      description:
        "Frequent travelers can benefit from a multi-trip travel insurance policy, which provides coverage for multiple trips throughout the year under a single plan. This is particularly useful for business professionals and frequent flyers, as it eliminates the hassle of buying separate insurance for each trip. It covers emergency medical expenses, baggage loss, trip cancellations, and other travel-related issues every time the policyholder travels within the coverage period.",
      icon: <FaRedo className="text-blue-600 text-3xl" />,
    },
    {
      title: "Family Travel Insurance",
      description:
        "Traveling with family involves added responsibilities, and this insurance ensures that all members are covered under a single policy. It offers financial protection against medical emergencies, trip cancellations, personal liabilities, and baggage loss for the entire family. Family travel insurance reduces the need for multiple individual policies and provides collective security, ensuring a stress-free journey for everyone.",
      icon: <FaUsers className="text-blue-600 text-3xl" />,
    },
    {
      title: "Group Travel Insurance",
      description:
        "This type of policy is designed for teams traveling together, such as corporate groups, pilgrimage groups, or student excursions. Instead of buying individual policies, group travel insurance offers uniform coverage for all members at a reduced premium. It protects against common risks like medical emergencies, trip interruptions, baggage delays, and accidental injury while traveling. The coverage terms can vary based on group size and travel purpose.",
      icon: <FaPeopleCarry className="text-blue-600 text-3xl" />,
    },
    {
      title: "Student Travel Insurance",
      description:
        "Specially designed for students traveling abroad for higher education, this insurance provides coverage beyond standard travel risks. In addition to medical emergencies, lost luggage, and trip cancellations, student travel insurance also covers study interruptions, sponsor protection, and bail bond assistance in case of legal troubles. It ensures that students can focus on their studies without financial concerns related to unexpected incidents.",
      icon: <FaGraduationCap className="text-blue-600 text-3xl" />,
    },
    {
      title: "Senior Citizen Travel Insurance",
      description:
        "Senior citizens have unique travel needs, including higher medical risks and additional assistance. This insurance plan caters to individuals above 60 years, covering medical emergencies, hospitalization, trip cancellations, and baggage loss. Since older travelers may require emergency health services while abroad, this policy often includes coverage for pre-existing conditions, medical evacuation, and even caregiver assistance in some cases.",
      icon: <FaUserAlt className="text-blue-600 text-3xl" />,
    },
    {
      title: "Business Travel Insurance",
      description:
        "Business travelers often have tight schedules and cannot afford disruptions. This insurance is tailored for professionals traveling for work-related purposes. It offers coverage for medical emergencies, trip delays, loss of important documents (such as passports or business papers), and trip cancellations due to unforeseen events. It ensures that work-related travel remains smooth and hassle-free, minimizing potential financial losses.",
      icon: <FaBriefcase className="text-blue-600 text-3xl" />,
    },
    {
      title: "Schengen Travel Insurance",
      description:
        "For individuals traveling to any of the 26 Schengen countries in Europe, travel insurance is a mandatory visa requirement. Schengen travel insurance ensures coverage for medical emergencies, hospitalization, travel delays, and loss of belongings within the Schengen region. Since medical expenses in Europe can be costly, this insurance provides financial assistance in case of any emergencies during the trip. Some policies even include compensation for trip disruptions due to unforeseen circumstances.",
      icon: <FaMapMarkedAlt className="text-blue-600 text-3xl" />,
    },
  ];

  const travelBenefits = [
    {
      title: "Mandatory Entry Requirement for Many Countries",
      description:
        "Several countries require proof of travel insurance before issuing a visa. It ensures that visitors can afford medical emergencies, trip disruptions, or unexpected incidents abroad.",
      icon: <FaCheckCircle className="text-blue-600 text-3xl" />,
    },
    {
      title: "Protection Against Lost, Stolen, or Damaged Luggage",
      description:
        "With over 28 million bags mishandled annually due to human or technical errors, travel insurance offers reimbursement for lost, stolen, or damaged luggage, reducing financial loss.",
      icon: <FaSuitcase className="text-blue-600 text-3xl" />,
    },
    {
      title: "Medical Coverage and Emergency Hospitalization",
      description:
        "Post-COVID-19, global hospitalization costs have risen by 30%. Travel insurance covers unexpected medical expenses, including hospital stays, doctor visits, and emergency treatments abroad.",
      icon: <FaHospital className="text-blue-600 text-3xl" />,
    },
    {
      title: "Compensation for Flight Delays and Cancellations",
      description:
        "Missed connections, delayed flights, and cancellations are common, especially in tourist-heavy countries. Travel insurance helps cover additional costs like rebooking fees, hotel stays, and food expenses.",
      icon: <FaPlaneSlash className="text-blue-600 text-3xl" />,
    },
    {
      title: "Security Against Passport Loss or Theft",
      description:
        "Losing a passport abroad can be distressing. Travel insurance assists in recovering or replacing essential travel documents, preventing trip disruptions.",
      icon: <FaPassport className="text-blue-600 text-3xl" />,
    },
    {
      title: "Coverage for Unexpected Health Issues",
      description:
        "Nearly 50% of international travelers experience some health issue during their trip. From minor illnesses to major emergencies, travel insurance ensures access to medical care without excessive expenses.",
      icon: <FaHeartbeat className="text-blue-600 text-3xl" />,
    },
    {
      title: "Added Safety for Your Home While You Travel",
      description:
        "Some travel insurance policies include home protection (fire/burglary insurance) while you are away, ensuring complete peace of mind during your journey.",
      icon: <FaHome className="text-blue-600 text-3xl" />,
    },
  ];
  const exclusionsData = {
    "Medical & Pre-existing Conditions": {
      exclusions: [
        "Treatment for pre-existing diseases unless covered by an add-on.",
        "Pregnancy-related expenses unless specifically included.",
        "Medical treatments sought abroad without a valid reason.",
        "Traveling against medical advice or after a terminal diagnosis.",
        "Injuries due to self-harm, suicide attempts, or drug/alcohol influence.",
      ],
      explanation:
        "Travel insurance does not cover medical issues that arise from pre-existing conditions, self-inflicted harm, or high-risk choices. If an individual ignores medical advice or travels solely for treatment, claims might be rejected. Always check if additional riders are needed for pregnancy or pre-existing conditions.",
    },
    "Travel Violations & Legal Exclusions": {
      exclusions: [
        "Traveling without a valid visa.",
        "Overstaying beyond the visa’s allowed duration.",
        "Traveling only for medical treatment.",
        "Breaking the law or being involved in criminal activities.",
        "Claims arising due to war, civil unrest, or terrorism.",
      ],
      explanation:
        "Travel insurance is invalid if the traveler violates visa regulations, stays beyond their permitted duration, or engages in illegal activities. Additionally, if someone knowingly travels to a conflict zone or an area under travel restrictions, claims related to war or terrorism might not be covered.",
    },
    "Adventure & Hazardous Activities Exclusions": {
      exclusions: [
        "Injuries from adventure sports like mountaineering, scuba diving, etc., unless covered by an add-on.",
        "Accidents occurring while performing hazardous jobs.",
        "Damage caused by engaging in dangerous activities.",
        "Losses due to reckless behavior or extreme sports.",
      ],
      explanation:
        "High-risk activities require special coverage. Standard travel insurance won’t cover injuries from extreme sports or hazardous jobs unless specific add-ons are purchased. It’s essential for thrill-seekers and professionals in risky fields to opt for tailored policies.",
    },
    "Baggage & Document Exclusions": {
      exclusions: [
        "Loss of baggage shipped separately or left unattended.",
        "Delay or failure in reporting lost luggage.",
        "Cost of obtaining a duplicate passport if the original was lost due to negligence.",
        "Claims beyond the geographical limit specified in the policy.",
      ],
      explanation:
        "Luggage and important documents must be handled responsibly. If a traveler ships their baggage separately, leaves it unattended, or fails to report a loss on time, insurance claims may not be approved. Additionally, policies often specify coverage regions, so travel beyond those areas won’t be insured.",
    },
  };
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };

  useEffect(() => {
    fetchInsuranceData();
  }, []);
  const fetchInsuranceData = async () => {
    try {
      const response = await Http.get(Insurance_API);

      // The response structure might have nested data
      // Check if response has an insurance property (like in the original fetch response)
      const data = response.insurance || response;

      // Filter the insurance data to show only Car category
      const carInsuranceData = Array.isArray(data)
        ? data.filter((insurance) => insurance.category_name === "Travel")
        : [];

      setInsuranceData(carInsuranceData);
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch insurance data");
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };
  // const fetchInsuranceData = async () => {
  //     try {
  //         const response = await fetch('https://test.thefinmaster.com/console/react-app-directory/insurance-api.php');
  //         if (!response.ok) {
  //             throw new Error('Network response was not ok');
  //         }
  //         const data = await response.json();
  //         setInsuranceData(data.insurance || []);
  //         setLoading(false);
  //     } catch (error) {
  //         setError('Failed to fetch insurance data');
  //         setLoading(false);
  //         console.error('Error fetching data:', error);
  //     }
  // };
  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '✈️', text: 'Suits All Domestic & Global Trips' },
    { icon: '📄', text: 'No Paperwork or Manual Input' },
    { icon: '📲', text: '  Smooth Experience on All Devices' },
    { icon: '🔍', text: ' Helps Choose Best Trip Coverage' },

  ];
  return (
    <>
      <Helmet>
        <title>
        Travel Insurance in India – Compare International & Domestic Plans
        </title>
        <meta
          name="description"
          content="Secure your trips with the best travel insurance. Compare domestic and international travel insurance plans and buy online."
        />
        <meta
          name="keywords"
          content="auto insurance coverages, automobile insurance companies, commercial auto insurance, icici lombard motor insurance, maruti insurance online"
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/travel_insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      {/* Your existing content */}
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>

          <li className="flex items-center">
            <span className="text-gray-500 ">Travel Insurance</span>
          </li>
          {/* <li className="flex items-center">
              <Link to="/credit-card" className="text-blue-600 hover:text-blue-800">Credit Card</Link>
              <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </li> */}
          {/* <li className="flex items-center">
              <span className="text-gray-500">Car Loan EMI Calculator</span>
            </li> */}
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-4xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-0 pl-6">
                  <span className="text-blue-600 ">
                    Explore Travel Insurance
                  </span>{" "}
                  Options Now!
                </h1>
                {/* <a href="#insurance_plans">
                  <button className="py-4 px-8  border rounded-full bg-blue-600 text-white shadow-lg hover:shadow-xl text-lg mt-10 font-medium">
                    See Plans
                  </button>
                </a> */}
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-4"
                src="../assets/travel111.png"
                alt="Noimage"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="shadow-lg rounded-lg p-4 md:p-8 lg:p-12 mb-6 px-6 mx-4 md:mx-8 lg:mx-10 text-gray-600 flex justify-center items-center">
        <p className="text-lg md:text-base lg:text-lg text-center">
          Select from a range of coverage options to customize your travel
          insurance plan according to your needs. Protect yourself from medical
          emergencies, trip cancellations, or lost luggage. Compare plans,
          understand coverage details, and receive free quotes—all in one place.
          We’ll help you find the ideal travel insurance plan to ensure a
          worry-free journey.{" "}
        </p>
      </div>
 
      <div className="container mx-auto">
      <div className="flex flex-col lg:flex-row">
        {/* Main content area with insurance listings */}
        <div className="w-full lg:w-3/4">
          <div id="insurance_plans" className="lg:pr-6 md:px-4 px-2">
            {loading ? (
              <div className="text-center py-10">Loading insurance options...</div>
            ) : error ? (
              <div className="text-center py-10 text-red-600">{error}</div>
            ) : insuranceData.length === 0 ? (
              <div className="text-center py-10">
                No car insurance options available at the moment.
              </div>
            ) : (
          insuranceData.map((insurance) => (
            <div
              key={insurance.id}
              className="w-full max-w-[900px] py-2 border shadow-lg overflow-hidden hover:shadow-xl rounded-lg flex flex-wrap md:flex-nowrap gap-2 md:gap-5 justify-between mx-auto mt-4"
            >
              <div className="flex flex-wrap md:flex-nowrap w-full justify-center items-center md:justify-normal">
                <Link to={`/travel_insurance/${insurance.page_url}`}>
                  <div className="w-[95%] md:w-[220px] overflow-hidden py-2 md:ml-2">
                    <img
                      className="w-full h-full object-contain"
                      alt={insurance.page_title || "Insurance Provider"}
                      src={insurance.image}
                    />
                  </div>
                </Link>
              </div>

              <div className="w-full md:pl-0 pl-0 mt-0 md:mt-6 text-center">
                <p className="text-gray-500 font-medium text-sm md:text-base">Premium Starting </p>
                <h4 className="text-xl md:text-2xl py-0.5 font-bold">
                  @ ₹{insurance.premium_start}
                </h4>
              </div>

              <div className="w-full md:pl-0 pl-0 mt-0 md:mt-6 text-center">
                <p className="text-gray-500 font-medium text-sm md:text-base">Baggage Loss</p>
                <h4 className="text-xl md:text-2xl py-0.5 font-bold">
                  {insurance.claim_settlement}
                </h4>
              </div>

              <div className="w-full md:pl-0 pl-0 mt-0 md:mt-6 text-center">
                <p className="text-gray-500 font-medium text-sm md:text-base">Medical expenses</p>
                <h4 className="text-xl md:text-2xl py-0.5 font-bold">
                  {insurance.network_garages}+
                </h4>
              </div>

              <div className="flex flex-wrap justify-center md:justify-between items-center my-5 w-full gap-2 px-4">
                <a
                  href={insurance.bank_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <button className="bg-green-500 text-white font-semibold py-2 px-4 rounded hover:bg-green-600 transition duration-300">
                    Get Quote!
                  </button>
                </a>

                <Link to={`/travel_insurance/${insurance.page_url}`}>
                  <button className="text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-300 bg-blue-500">
                  See Details
                  </button>
                </Link>
              </div>
            </div>
          ))
        )}
      </div>
      </div>
<div className="w-full lg:w-1/4 px-2 md:px-4 mt-6 lg:mt-0">
          <div className="sticky top-20">
            {/* Ad 1 - Visible on all screen sizes */}
            <a href="https://play.google.com/store/apps/details?id=finance.global.travel.niyo&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="block">
              <img 
                src="/assets/new.png" 
                alt="Insurance Special Offer" 
                className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              />
            </a>
            
            {/* Ad 2 - Hidden on small mobile, visible on larger screens */}
            {/* <a href="https://example.com/premium-discount" target="_blank" rel="noopener noreferrer" className="hidden sm:block mt-6">
              <img 
                src="/assets/niyoo.png" 
                alt="Premium Discount Offer" 
                className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              />
            </a> */}
            
            {/* Compact ad for mobile only */}
            {/* <a href="https://example.com/mobile-offer" target="_blank" rel="noopener noreferrer" className="block sm:hidden mt-4">
              <img 
                src="/assets/niyoo.png" 
                alt="Mobile Special Offer" 
                className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              />
            </a> */}
          </div>
        </div>
      </div>
    </div>

      <div className="flex justify-center items-center  p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center ">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            What is travel insurance?
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Travel insurance safeguards your journey from unexpected financial
            losses and travel-related emergencies. It provides coverage for
            unforeseen situations such as trip cancellations, medical
            emergencies, lost baggage, flight delays, and passport loss,
            ensuring a smooth travel experience. Whether you are traveling
            domestically or internationally, this insurance acts as a financial
            shield from the moment you depart until you return home. Travel
            insurance is suitable for all types of travelers, including solo
            explorers, families, students, and senior citizens. No matter your
            destination or purpose of travel, having the right policy ensures
            peace of mind throughout your journey.
          </p>
        </div>
      </div>
   <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-6"> 
  <div className="flex flex-col md:flex-row justify-between items-center gap-10">
    {/* Left - Text and CTA */}
    <div className="max-w-xl space-y-6 text-center md:text-left">
      <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
        Calculate Your Travel Insurance <br />
        in <span className="text-blue-600">Seconds</span>
      </h2>
      <p className="text-gray-700 text-lg">
        Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
      </p>
      <Link
        to="/financial-tools-calculators/travel-insurance-calculator"
        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
      >
        Start Calculating <ArrowRight className="w-5 h-5" />
      </Link>
    </div>

    {/* Right - Feature List with Emojis */}
    <div className="space-y-4 text-left">
      {features.map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <span className="text-xl">{item.icon}</span>
          <span className="text-gray-800 font-medium">{item.text}</span>
        </div>
      ))}
    </div>
  </div>
</section>
      <div className=" flex flex-col items-center py-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-bold mb-10 text-center">
          Key Stats of a Travel Insurance
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl w-full px-4 md:px-2">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="p-6 border border-gray-300 rounded-2xl shadow-lg flex flex-col items-center text-center"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              {stat.icon}
              <h3 className="text-2xl font-semibold mb-3">{stat.title}</h3>
              <p className="text-gray-700">{stat.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
      <div className=" px-2 md:px-6 bg-gray-100">
        <div className=" p-6 min-h-screen">
          <h1 className="text-2xl md:text-3xl font-bold text-blue-700 mb-4">
            Choosing the Right Travel Insurance
          </h1>
          <p className="text-gray-700 mb-6">
            Selecting the right type of travel insurance depends on factors such
            as the destination, travel frequency, the number of travelers, and
            specific requirements. Whether you’re a solo traveler, student,
            business professional, or family, having travel insurance offers
            financial security and peace of mind, ensuring that you can handle
            unexpected situations efficiently while on your journey.
          </p>

          {travelInsuranceData.map((point, index) => (
            <div
              key={index}
              className="mb-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <div className="mb-4">{point.icon}</div>
              <h2 className="text-xl font-semibold text-blue-600">
                {point.title}
              </h2>
              <p className="text-gray-600">{point.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className=" px-2 md:px-6 bg-gray-100">
        <div className=" p-6 min-h-screen">
          <h1 className="text-2xl md:text-3xl font-bold text-blue-700 mb-4">
            Choosing the Right Travel Insurance
          </h1>
          <p className="text-gray-700 mb-6">
            Selecting the right type of travel insurance depends on factors such
            as the destination, travel frequency, the number of travelers, and
            specific requirements. Whether you’re a solo traveler, student,
            business professional, or family, having travel insurance offers
            financial security and peace of mind, ensuring that you can handle
            unexpected situations efficiently while on your journey.
          </p>

          {travelBenefits.map((point, index) => (
            <div
              key={index}
              className="mb-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <div className="mb-4">{point.icon}</div>
              <h2 className="text-xl font-semibold text-blue-600">
                {point.title}
              </h2>
              <p className="text-gray-600">{point.description}</p>
            </div>
          ))}
        </div>
      </div>

      <TravelCover />
      <div className="p-6 flex flex-col items-center">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center text-black">
          What's Not Covered Under Travel Insurance?
        </h2>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          {Object.keys(exclusionsData).map((tab) => (
            <button
              key={tab}
              className={`px-6 py-3 rounded-full text-lg font-medium transition duration-300 ease-in-out focus:ring-2 focus:ring-blue-400 
          ${
            selectedTab === tab
              ? "bg-blue-600 text-white shadow-lg"
              : "bg-gray-200 text-black hover:bg-gray-400"
          }`}
              onClick={() => setSelectedTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Exclusions List */}
        <div className="w-full max-w-7xl p-6 border rounded-xl bg-gray-50 shadow-lg ">
          <h3 className="text-2xl font-semibold mb-3 text-blue-700">
            {selectedTab}
          </h3>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
            {exclusionsData[selectedTab].exclusions.map((item, index) => (
              <li key={index} className="leading-relaxed">
                {item}
              </li>
            ))}
          </ul>
          <p className="text-gray-700 font-semibold leading-relaxed">
            {exclusionsData[selectedTab].explanation}
          </p>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Heading */}
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">
          Requirements to Buy Travel Insurance
        </h2>

        {/* Introduction */}
        <p className="text-gray-700 mb-4 text-center">
          Purchasing travel insurance is a straightforward process, but you must
          meet certain eligibility criteria and provide the required details to
          ensure hassle-free coverage. Below are the key requirements:
        </p>

        {/* Section 1 - Eligibility Criteria */}
        <div className="bg-gray-100 p-5 rounded-lg shadow-md mb-6">
          <h3 className="text-xl font-semibold mb-3">
            1. Eligibility Criteria
          </h3>
          <p className="text-gray-700 mb-4">
            Before buying a travel insurance policy, ensure that you meet the
            eligibility requirements based on the type of insurance you need:
          </p>

          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>
              <strong>General Travel Insurance:</strong> Available for
              individuals aged between 0.6 and 70 years.
            </li>
            <li>
              <strong>Single-Trip Travel Insurance:</strong> Covers one-time
              trips for adults aged 18-70 years and children aged 3 months and
              above.
            </li>
            <li>
              <strong>Family Travel Insurance:</strong> Covers a family of four,
              including the insured, spouse, and two dependents. Adults must be
              18-60 years old, and children must be under 21 years.
            </li>
            <li>
              <strong>Senior Citizen Travel Insurance:</strong> Available for
              individuals aged 61 to 90+ years.
            </li>
            <li>
              <strong>Student Travel Insurance:</strong> Must be enrolled in an
              educational institution abroad, with an age limit of 16-35 years
              (varies by plan).
            </li>
            <li>
              <strong>Corporate Travel Insurance:</strong> Available for
              professionals between 18 and 70 years.
            </li>
            <li>
              <strong>Multi-Trip Travel Insurance:</strong> Designed for
              frequent travelers aged 18-70 years, with renewable coverage for
              children aged 3 months and above.
            </li>
          </ul>
        </div>

        {/* Section 2 - Required Documents & Information */}
        <div className="bg-gray-100 p-5 rounded-lg shadow-md">
          <h3 className="text-xl font-semibold mb-3">
            2. Required Documents & Information
          </h3>
          <p className="text-gray-700 mb-4">
            To purchase travel insurance, you will need to provide the following
            details:
          </p>

          <ul className="list-none space-y-3 text-gray-700">
            <li>
              ✅ <strong>Personal Details:</strong> Full name, age, address, and
              contact details.
            </li>
            <li>
              ✅ <strong>Travel Information:</strong> Destination(s) and travel
              dates.
            </li>
            <li>
              ✅ <strong>Health Details:</strong> Any pre-existing medical
              conditions, if applicable.
            </li>
            <li>
              ✅ <strong>Passport Information:</strong> Passport number(s) of
              all travelers included in the policy.
            </li>
          </ul>
        </div>

        {/* Conclusion */}
        <p className="text-gray-700 text-center mt-6 font-semibold">
          By fulfilling these basic requirements, you can secure the right
          travel insurance policy for a worry-free trip.
        </p>
      </div>
      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h1 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default TravelInsuranceNew;
